/**
 * Formats prices injected by third-party apps to match theme market rules.
 *
 * Label-fix apps (Hello Retail, FoxKit in-cart upsell, …): amounts are already
 * in the active market currency — strip trailing ,00/.00 and fix currency
 * labels (kr vs NOK/DKK/…; never leave "SEK" on SEK market).
 *
 * Omnibus: widget.js always prints Number(price).toFixed(2) + currency, and
 * data-lowest_price is shop/SEK major units. We rewrite the widget from its
 * data-* attributes so amounts convert and whole numbers have no ,00/.00.
 */
const APP_LABEL_PRICE_SELECTOR = [
  // Hello Retail / recommendation widgets (hr-recom-*, aw-recom-*, …)
  '[id^="hr-recom-"]',
  '[id^="aw-recom-"]',
  '[id*="-recom-"]',
  '[class*="hr-recom"]',
  '[class*="aw-recom"]',
  '.hr-results',
  '.search__content',
  '[class*="helloretail"]',
  '[id*="helloretail"]',
  // FoxKit cart drawer upsell ("others also purchased")
  'foxkit-incart-upsell',
  '[class*="foxkit"]',
  '[class*="FoxKit"]',
  '[id*="foxkit"]',
  // Whole cart drawer — catches late-injected app prices showing SEK
  'cart-drawer',
  '.cart-drawer',
].join(', ');

let scanScheduled = false;
const pendingRoots = new Set();

function getActiveCurrency() {
  return (
    window.Shopify?.currency?.active ||
    window.FoxTheme?.settings?.currencyCode ||
    ''
  ).toUpperCase();
}

function getCurrencyRate() {
  const rate = parseFloat(window.Shopify?.currency?.rate ?? '1');
  return Number.isFinite(rate) && rate > 0 ? rate : 1;
}

function stripTrailingZeroDecimals(text) {
  if (!text) return text;

  return String(text)
    .replace(/([.,])\s*00(?!\d)/g, '')
    .replace(/\u00a0/g, ' ');
}

function formatMarketMoney(amountMajor) {
  const cents = Math.round(Number(amountMajor) * 100);
  if (!Number.isFinite(cents)) return '';

  const currencyCode = getActiveCurrency();
  const whole = Math.trunc(cents / 100);
  const fraction = Math.abs(cents % 100);

  let numberPart = String(Math.abs(whole)).replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ' '
  );

  if (fraction !== 0) {
    numberPart += `,${String(fraction).padStart(2, '0')}`;
  }

  if (whole < 0) {
    numberPart = `-${numberPart}`;
  }

  if (currencyCode === 'SEK') {
    return `${numberPart} kr`;
  }

  return currencyCode ? `${numberPart} ${currencyCode}` : numberPart;
}

function formatAppLabelPriceText(text) {
  if (!text) return text;

  const currencyCode = getActiveCurrency();
  let formatted = stripTrailingZeroDecimals(text);

  if (!currencyCode) {
    return formatted;
  }

  if (currencyCode === 'SEK') {
    return stripTrailingZeroDecimals(formatted.replace(/\bSEK\b/gi, 'kr'));
  }

  return stripTrailingZeroDecimals(
    formatted
      .replace(/\bSEK\b/g, currencyCode)
      .replace(/(\d)\s*kr\.?(?!\w)/gi, `$1 ${currencyCode}`)
      .replace(/\bkr\.?\s+(?=\d)/gi, `${currencyCode} `)
  );
}

function shouldFormatAppLabelPriceText(text) {
  if (!text) return false;

  return (
    /[.,]00(?!\d)/.test(text) ||
    /\bSEK\b/i.test(text) ||
    (getActiveCurrency() !== 'SEK' && /\bkr\.?(?!\w)/i.test(text))
  );
}

function formatAppLabelPriceRoot(root) {
  if (!root) return;

  const walk = (nodeRoot) => {
    const walker = document.createTreeWalker(nodeRoot, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        return shouldFormatAppLabelPriceText(node.nodeValue)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    });

    let node;

    while ((node = walker.nextNode())) {
      const formatted = formatAppLabelPriceText(node.nodeValue);

      if (formatted !== node.nodeValue) {
        node.nodeValue = formatted;
      }
    }

    // Hello Retail / FoxKit may render inside open shadow roots.
    if (nodeRoot.querySelectorAll) {
      nodeRoot.querySelectorAll('*').forEach((el) => {
        if (el.shadowRoot) {
          walk(el.shadowRoot);
        }
      });
    }
  };

  walk(root);
}

/**
 * Rebuild Omnibus widget HTML from data-* (source of truth).
 * widget.js uses toFixed(2) which always creates .00 — we never do that.
 */
function formatOmnibusWidget() {
  const widget = document.getElementById('ow_omnibus_widget');
  if (!widget) return;

  const lowestPrice = Number(widget.dataset.lowest_price);
  const currentPrice = Number(widget.dataset.current_price);
  const highestPrice = Number(widget.dataset.highest_price);
  const lowestTranslation = widget.dataset.lowest_translation || '';
  const originalTranslation = widget.dataset.original_translation || '';
  const hideLowestIfEqual = widget.dataset.hide_lowest_if_equal === 'true';
  const hideIfNotOnSale = widget.dataset.hide_if_not_on_sale === 'true';
  const showHighestPrice = widget.dataset.show_highest_price === 'true';
  const hideMaxIfEqual = widget.dataset.hide_max_if_equal === 'true';
  const isHiddenVendor = widget.dataset.is_hidden_vendor === 'true';
  const isHiddenTag = widget.dataset.is_hidden_tag === 'true';

  if (isHiddenTag || isHiddenVendor) {
    widget.innerHTML = '';
    return;
  }

  if (!Number.isFinite(lowestPrice)) return;

  const rate = getCurrencyRate();
  const onSale =
    Number.isFinite(highestPrice) &&
    Number.isFinite(currentPrice) &&
    highestPrice > currentPrice;
  const lowestIsCurrent =
    Number.isFinite(currentPrice) && currentPrice === lowestPrice;
  const highestIsCurrent =
    Number.isFinite(currentPrice) &&
    Number.isFinite(highestPrice) &&
    currentPrice === highestPrice;

  let content = '';

  if (!(hideIfNotOnSale && !onSale) && !(hideLowestIfEqual && lowestIsCurrent)) {
    content += `<p style="margin: 0;">${lowestTranslation} ${formatMarketMoney(
      lowestPrice * rate
    )}</p>`;
  }

  if (showHighestPrice && Number.isFinite(highestPrice)) {
    if (!(hideMaxIfEqual && highestIsCurrent)) {
      content += `<p style="margin: 0;">${originalTranslation} ${formatMarketMoney(
        highestPrice * rate
      )}</p>`;
    }
  }

  if (widget.innerHTML !== content) {
    widget.innerHTML = content;
  }
}

function queueAppLabelPriceRoot(root) {
  if (!root) return;

  pendingRoots.add(root);

  if (scanScheduled) return;

  scanScheduled = true;

  requestAnimationFrame(() => {
    scanScheduled = false;

    pendingRoots.forEach(formatAppLabelPriceRoot);
    pendingRoots.clear();
  });
}

function findAppLabelPriceRoots(node) {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }

  const roots = [];

  if (node.matches(APP_LABEL_PRICE_SELECTOR)) {
    roots.push(node);
  }

  const parentRoot = node.closest(APP_LABEL_PRICE_SELECTOR);

  if (parentRoot) {
    roots.push(parentRoot);
  }

  node.querySelectorAll(APP_LABEL_PRICE_SELECTOR).forEach((root) => {
    roots.push(root);
  });

  return roots;
}

function hookOmnibusUpdater() {
  if (window.__omnibusMarketPatched) {
    formatOmnibusWidget();
    return;
  }

  window.__omnibusMarketPatched = true;

  const previous = window.updateOmnibusWidget;

  window.updateOmnibusWidget = function patchedUpdateOmnibusWidget() {
    if (typeof previous === 'function' && previous !== patchedUpdateOmnibusWidget) {
      previous();
    }

    formatOmnibusWidget();
  };

  formatOmnibusWidget();
}

function startAppPriceFormatter() {
  document.querySelectorAll(APP_LABEL_PRICE_SELECTOR).forEach(queueAppLabelPriceRoot);
  hookOmnibusUpdater();

  const observer = new MutationObserver((mutations) => {
    let omnibusTouched = false;

    mutations.forEach((mutation) => {
      if (mutation.type === 'characterData') {
        const root = mutation.target.parentElement?.closest(
          APP_LABEL_PRICE_SELECTOR
        );

        if (root) {
          queueAppLabelPriceRoot(root);
        }

        if (mutation.target.parentElement?.closest('#ow_omnibus_widget')) {
          omnibusTouched = true;
        }

        return;
      }

      mutation.addedNodes.forEach((node) => {
        findAppLabelPriceRoots(node).forEach(queueAppLabelPriceRoot);

        if (
          node.nodeType === Node.ELEMENT_NODE &&
          (node.id === 'ow_omnibus_widget' ||
            node.querySelector?.('#ow_omnibus_widget'))
        ) {
          omnibusTouched = true;
        }
      });

      if (
        mutation.target?.id === 'ow_omnibus_widget' ||
        mutation.target?.closest?.('#ow_omnibus_widget')
      ) {
        omnibusTouched = true;
      }
    });

    if (omnibusTouched) {
      // Defer so we run after Omnibus widget.js finishes writing innerHTML.
      requestAnimationFrame(formatOmnibusWidget);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });
}

function boot() {
  startAppPriceFormatter();

  // widget.js binds its own local fn on DOMContentLoaded (not window.*) —
  // run again on the next frame so we win after it paints "89.00".
  requestAnimationFrame(() => {
    hookOmnibusUpdater();
    formatOmnibusWidget();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}